
// src/pages/WebsiteBackups.js
import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Spinner, Alert, Modal as BootstrapModal } from 'react-bootstrap';
import { websiteService } from '../api/services/websites';
import WebsiteOverview from "./WebsiteOverview";

const WebsiteBackups = ({ website }) => {
    const [backups, setBackups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [isRestoring, setIsRestoring] = useState(false);
    const [selectedBackup, setSelectedBackup] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [backupToRestore, setBackupToRestore] = useState(null);

    useEffect(() => {
        const fetchBackups = async () => {
            try {
                const { data } = await websiteService.getBackups(website.id);
                setBackups(data);
                setLoading(false);
            } catch (err) {
                setError('Nepodařilo se načíst zálohy');
                setLoading(false);
            }
        };

        fetchBackups();
    }, [website.id]);

    const handleCreateBackup = async () => {
        setIsCreating(true);
        try {
            const { data } = await websiteService.createBackup(website.id);
            setBackups([data, ...backups]);
        } catch (err) {
            setError('Vytvoření zálohy selhalo');
        } finally {
            setIsCreating(false);
        }
    };

    const openRestoreModal = (backupId) => {
        setBackupToRestore(backupId);
        setShowConfirmModal(true);
    };

    const handleRestoreBackup = async () => {
        setShowConfirmModal(false);
        if (!backupToRestore) return;

        setIsRestoring(true);
        setSelectedBackup(backupToRestore);
        try {
            await websiteService.restoreBackup(website.id, backupToRestore);
            // Po úspěšné obnově bychom mohli přidat notifikaci
        } catch (err) {
            setError('Obnovení ze zálohy selhalo');
        } finally {
            setIsRestoring(false);
            setSelectedBackup(null);
            setBackupToRestore(null);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Načítání záloh...</span>
                </Spinner>
            </div>
        );
    }

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3>Zálohy webu</h3>
                <Button
                    variant="primary"
                    onClick={handleCreateBackup}
                    disabled={isCreating}
                >
                    {isCreating ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                            Vytváření zálohy...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-plus me-2"></i> Vytvořit novou zálohu
                        </>
                    )}
                </Button>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            <Card className="shadow-sm">
                <Card.Body>
                    {backups.length === 0 ? (
                        <div className="text-center py-5">
                            <i className="fas fa-database fa-4x text-secondary mb-3"></i>
                            <h5>Zatím nebyly vytvořeny žádné zálohy</h5>
                            <p className="text-muted">Vytvořte první zálohu tlačítkem výše</p>
                        </div>
                    ) : (
                        <Table responsive hover>
                            <thead>
                            <tr>
                                <th>Datum vytvoření</th>
                                <th>Velikost</th>
                                <th>Typ</th>
                                <th>Poznámka</th>
                                <th>Akce</th>
                            </tr>
                            </thead>
                            <tbody>
                            {backups.map((backup) => (
                                <tr key={backup.id}>
                                    <td>{new Date(backup.createdAt).toLocaleString()}</td>
                                    <td>{backup.size}</td>
                                    <td>{backup.type === 'full' ? 'Kompletní' : 'Pouze databáze'}</td>
                                    <td>{backup.note || '-'}</td>
                                    <td>
                                        <div className="d-flex gap-2">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => openRestoreModal(backup.id)}
                                                disabled={isRestoring}
                                            >
                                                {isRestoring && selectedBackup === backup.id ? (
                                                    <>
                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                                        Obnovování...
                                                    </>
                                                ) : (
                                                    'Obnovit'
                                                )}
                                            </Button>
                                            <a
                                                href={`/api/websites/backup/${website.id}/download/${backup.id}`}
                                                className="btn btn-outline-success btn-sm"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Stáhnout
                                            </a>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            {/* Confirmation Modal */}
            <BootstrapModal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
                <BootstrapModal.Header closeButton>
                    <BootstrapModal.Title>Potvrzení obnovení</BootstrapModal.Title>
                </BootstrapModal.Header>
                <BootstrapModal.Body>
                    <p>Opravdu chcete obnovit web z této zálohy? Tato akce přepíše všechna aktuální data.</p>
                    <Alert variant="warning">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        Tento proces nelze vrátit zpět!
                    </Alert>
                </BootstrapModal.Body>
                <BootstrapModal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Zrušit
                    </Button>
                    <Button variant="danger" onClick={handleRestoreBackup}>
                        Ano, obnovit
                    </Button>
                </BootstrapModal.Footer>
            </BootstrapModal>
        </>
    );
};
export default WebsiteBackups;